/**
 * WelcomeScreen — first-run front door (guest-first onboarding), styled to the
 * onboarding design: full-bleed stadium hero, the animated Zporti mascot, the "zporti"
 * wordmark, and two CTAs.
 *
 * Presentational: the caller wires navigation.
 *  - Get Started → tutorial → guest mode (no sign-up wall).
 *  - I already have an account → sign-in (behaviour unchanged).
 *
 * See specs/guest-first-onboarding/spec.md (Story 1).
 */

import { View, Text, Pressable, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { AnimatedMonkey } from '../AnimatedMonkey/AnimatedMonkey';
import { ZportiWordmark } from './ZportiWordmark';
import {
  welcomeStyles,
  WELCOME_SCRIM_COLORS,
  WELCOME_SCRIM_LOCATIONS,
} from './WelcomeScreen.styles';
import { WELCOME_TEST_IDS, WELCOME_COPY, WELCOME_BACKGROUND_URI } from './WelcomeScreen.constants';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';

interface WelcomeScreenProps {
  onGetStarted: () => void;
  onHaveAccount: () => void;
  testID?: string;
}

export function WelcomeScreen({ onGetStarted, onHaveAccount, testID }: WelcomeScreenProps) {
  return (
    <View style={welcomeStyles.container} testID={testID ?? WELCOME_TEST_IDS.CONTAINER}>
      <Image
        source={{ uri: WELCOME_BACKGROUND_URI }}
        style={welcomeStyles.backgroundImage}
        alt={WEB_IMAGE_ALT_DECORATIVE}
        contentFit="cover"
      />
      <LinearGradient
        colors={WELCOME_SCRIM_COLORS}
        locations={WELCOME_SCRIM_LOCATIONS}
        style={welcomeStyles.scrim}
        pointerEvents="none"
      />

      <SafeAreaView style={welcomeStyles.content} edges={['top', 'bottom', 'left', 'right']}>
        <View style={welcomeStyles.hero}>
          <AnimatedMonkey style={welcomeStyles.mascot} accessibilityLabel={WELCOME_COPY.BRAND} />
          <ZportiWordmark height={46} />
          <Text style={welcomeStyles.subline}>{WELCOME_COPY.SUBLINE}</Text>
        </View>

        <View style={welcomeStyles.actions}>
          <Pressable
            onPress={onGetStarted}
            testID={WELCOME_TEST_IDS.GET_STARTED}
            accessibilityRole="button"
            accessibilityLabel={WELCOME_COPY.GET_STARTED}
            style={({ pressed, hovered }) => [
              welcomeStyles.primaryButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && welcomeStyles.primaryButtonPressed,
              hovered && Platform.OS === 'web' && welcomeStyles.primaryButtonHoveredWeb,
            ]}
          >
            <Text style={welcomeStyles.primaryButtonText}>{WELCOME_COPY.GET_STARTED}</Text>
          </Pressable>

          <Pressable
            onPress={onHaveAccount}
            testID={WELCOME_TEST_IDS.HAVE_ACCOUNT}
            accessibilityRole="button"
            accessibilityLabel={WELCOME_COPY.HAVE_ACCOUNT}
            style={({ pressed, hovered }) => [
              welcomeStyles.secondaryButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && welcomeStyles.secondaryButtonPressed,
              hovered && Platform.OS === 'web' && welcomeStyles.secondaryButtonHoveredWeb,
            ]}
          >
            <Text style={welcomeStyles.secondaryButtonText}>{WELCOME_COPY.HAVE_ACCOUNT}</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    </View>
  );
}
